import React, { useEffect, useState, useCallback } from 'react';
import { useApolloClient, QueryHookOptions } from '@apollo/react-hooks';

import * as ApolloReactCommon from '@apollo/react-common';
import { NetworkStatus } from 'apollo-client';
import type { ApolloError } from 'apollo-client';

import Event from 'geval/event';

import createContextHelpers from 'shared/helpers/context';

type QueryDataResult<Q, V> = ApolloReactCommon.QueryResult<Q, V>['data'];

type CachedQueryResult<Q, V> = {
  loading: boolean;
  data?: QueryDataResult<Q, V>;
  refetch: () => Promise<CachedQueryResult<Q, V>>;
  error?: ApolloError;
  variables?: V;
  networkStatus: NetworkStatus;
};

type CacheEntry<Q, V> = {
  listen: () => () => void;
  hasRun: boolean;
  result: CachedQueryResult<Q, V>;
};

type CacheEntryMap = { [x in string]: CacheEntry<unknown, unknown> };

export const queryStore = { cache: new WeakMap<any, CacheEntryMap>() };

const QueryStoreContext = React.createContext(queryStore);
export const { QueryStoreProvider, useQueryStore, withQueryStore } = createContextHelpers(
  QueryStoreContext,
  `queryStore`
);

function getCircularReplacer(): (key: string, value: any) => any {
  const seen = new WeakSet();
  return (_key: string, value: any) => {
    if (typeof value === 'object' && value !== null) {
      const obj = value as object;
      if (seen.has(obj)) {
        return '[Circular]';
      }
      seen.add(obj);
    }
    return value;
  };
}

function createCacheEntry<Q, V>(apolloClient, operation, options?: QueryHookOptions<Q, V>): CacheEntry<Q, V> {
  const event = Event();
  const entry: CacheEntry<Q, V> = {
    listen: event.listen,
    hasRun: false,
    result: {
      loading: false,
      variables: options?.variables,
      refetch,
      networkStatus: NetworkStatus.loading,
    },
  };

  async function refetch(): Promise<CachedQueryResult<Q, V>> {
    entry.result.loading = true;
    if (entry.hasRun) {
      entry.result.networkStatus = NetworkStatus.refetch;
    } else {
      entry.result.networkStatus = NetworkStatus.loading;
    }
    entry.hasRun = true;

    try {
      const result = await apolloClient.query({
        query: operation,
        ...options,
      });
      entry.result.data = result.data;
      entry.result.error = result.error;
      entry.result.networkStatus = NetworkStatus.ready;
    } catch (error) {
      entry.result.networkStatus = NetworkStatus.error;
      entry.result.error = error;
    }
    entry.result.loading = false;

    // must be async, apolloClient.query isn't always async so we need to dezalgo right here
    setTimeout(() => event.broadcast(entry));
    return entry.result;
  }

  return entry;
}

export function useQuery<Q, V>(operation, options?: QueryHookOptions<Q, V>): CachedQueryResult<Q, V> {
  const [, setCount] = useState(0);
  const apolloClient = useApolloClient();
  const store = useQueryStore();

  const variablesString = JSON.stringify(options?.variables, getCircularReplacer());

  const rerender = useCallback(() => setCount((prevCount) => prevCount + 1), []);

  let cacheMap = store.cache.get(operation);
  if (!cacheMap) {
    cacheMap = {};
    store.cache.set(operation, cacheMap);
  }
  let cacheEntry = cacheMap[variablesString];

  if (!cacheEntry) {
    cacheEntry = createCacheEntry(apolloClient, operation, options);
    cacheMap[variablesString] = cacheEntry;
  }

  if (!cacheEntry.hasRun && !options?.skip) {
    // trigger initial fetch
    cacheEntry.result.refetch();
  }

  // listen on changes
  useEffect(() => {
    if (!options?.skip) {
      // listen function returns an unlistener function
      // so returning it will unregister us on unmount or re-render
      return cacheEntry.listen(rerender);
    }
    // consistent-return requirements
    // but we do want to return nothing. we only want to register for re-render when we're not skipping
    return undefined;
  }, [options?.skip, cacheEntry, rerender]);

  try {
    const data = apolloClient.readQuery({
      query: operation,
      variables: options?.variables,
    });
    return { ...cacheEntry.result, data };
  } catch (error) {
    if (error.name === 'Invariant Violation' && error.message.startsWith("Can't find field ")) {
      return cacheEntry.result;
    }

    return { ...cacheEntry.result, error };
  }
}
